import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ROOT_API_URL } from "../config/apiConfig"
import { companySiteSystemAlertApi } from "../api/systemAlertApi"

const resolveAttachmentUrl = (attachment) => {
  if (!attachment?.url) throw new Error("Alert attachment URL is missing")
  if (/^https?:\/\//i.test(attachment.url)) return attachment.url
  if (!ROOT_API_URL) throw new Error("REACT_APP_ROOT_API_URL is not configured")
  return `${ROOT_API_URL.replace(/\/$/, "")}${attachment.url.startsWith("/") ? attachment.url : `/${attachment.url}`}`
}

const formatDateTime = (value) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "Invalid alert date/time"
  return date.toLocaleString()
}

export default function SystemAlertBell() {
  const [canRead, setCanRead] = useState(null)
  const [alerts, setAlerts] = useState([])
  const [open, setOpen] = useState(false)
  const [selectedAlert, setSelectedAlert] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const rootRef = useRef(null)
  const autoOpenedAlertIds = useRef(new Set())

  useEffect(() => {
    let active = true

    const loadPermission = async () => {
      try {
        const response = await companySiteSystemAlertApi.getSidebar()
        if (!response.data?.menu) throw new Error("Sidebar API did not return a permission menu")
        const systemAlertModule = response.data.menu["system-alerts"]
        if (active) setCanRead(systemAlertModule?.permissions?.read === true)
      } catch (requestError) {
        if (active) {
          setCanRead(false)
          setError(requestError.message)
        }
      }
    }

    loadPermission()
    return () => {
      active = false
    }
  }, [])

  const loadAlerts = useCallback(async () => {
    if (canRead !== true) return

    try {
      const response = await companySiteSystemAlertApi.listActive()
      if (!Array.isArray(response.data)) throw new Error("System alert API returned an invalid alert list")
      setAlerts(response.data)
      setError("")
    } catch (requestError) {
      setAlerts([])
      setError(requestError.message)
    }
  }, [canRead])

  useEffect(() => {
    if (canRead !== true) return undefined

    loadAlerts()
    const intervalId = window.setInterval(loadAlerts, 60000)
    return () => window.clearInterval(intervalId)
  }, [canRead, loadAlerts])

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false)
    }
    document.addEventListener("mousedown", closeOnOutsideClick)
    return () => document.removeEventListener("mousedown", closeOnOutsideClick)
  }, [])

  const unreadCount = useMemo(() => alerts.filter((alert) => alert.status === "New").length, [alerts])

  const openAlert = useCallback(async (alertId) => {
    setLoading(true)
    setError("")
    try {
      const detailResponse = await companySiteSystemAlertApi.get(alertId)
      let alert = detailResponse.data
      if (!alert?._id) throw new Error("System alert details are missing")
      if (alert.status === "New") {
        const readResponse = await companySiteSystemAlertApi.markRead(alertId)
        alert = readResponse.data
      }
      const attachments = alert.attachments.map((attachment) => ({
        ...attachment,
        resolvedUrl: resolveAttachmentUrl(attachment),
      }))
      setSelectedAlert({ ...alert, attachments })
      setAlerts((current) => current.map((item) => (item._id === alertId ? { ...item, status: "Read" } : item)))
      setOpen(false)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (selectedAlert || loading) return

    const nextUnreadAlert = alerts.find(
      (alert) => alert.status === "New" && !autoOpenedAlertIds.current.has(alert._id),
    )
    if (!nextUnreadAlert) return

    autoOpenedAlertIds.current.add(nextUnreadAlert._id)
    openAlert(nextUnreadAlert._id)
  }, [alerts, loading, openAlert, selectedAlert])

  if (canRead !== true) return null

  return (
    <>
      <div className="position-relative" ref={rootRef}>
        <button type="button" className="btn btn-sm border-0 position-relative" title="System Alerts" aria-label={`System alerts${unreadCount ? `, ${unreadCount} unread` : ""}`} onClick={() => setOpen((value) => !value)}>
          <i className="bi bi-bell-fill" style={{ color: "#004a87", fontSize: "1.15rem" }} />
          {unreadCount > 0 && <span className="badge bg-danger rounded-pill position-absolute top-0 start-100 translate-middle">{unreadCount}</span>}
        </button>
        {open && (
          <div className="position-absolute end-0 mt-2 bg-white border rounded shadow text-dark" style={{ width: 360, maxWidth: "calc(100vw - 24px)", zIndex: 1100 }}>
            <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
              <strong>System Alerts</strong>
              <button type="button" className="btn btn-sm btn-link text-decoration-none" onClick={loadAlerts}>Refresh</button>
            </div>
            {error && <div className="alert alert-danger m-2 py-2">{error}</div>}
            {!error && alerts.length === 0 && <div className="text-muted text-center p-4">No active alerts.</div>}
            <div style={{ maxHeight: 380, overflowY: "auto" }}>
              {alerts.map((alert) => (
                <button key={alert._id} type="button" className="w-100 border-0 border-bottom bg-white text-start px-3 py-3" onClick={() => openAlert(alert._id)} disabled={loading}>
                  <div className="d-flex justify-content-between gap-2">
                    <strong className={alert.status === "New" ? "text-primary" : "text-dark"}>{alert.title}</strong>
                    {alert.status === "New" && <span className="badge bg-primary align-self-start">New</span>}
                  </div>
                  <small className="text-muted">{alert.severityLabel} | {formatDateTime(alert.startsAt)}</small>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {selectedAlert && (
        <div className="modal d-block" role="dialog" aria-modal="true" aria-labelledby="site-system-alert-title" style={{ background: "rgba(0,0,0,.48)", zIndex: 2000 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <span className="badge bg-primary mb-2">{selectedAlert.severityLabel}</span>
                  <h4 className="modal-title" id="site-system-alert-title">{selectedAlert.title}</h4>
                </div>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setSelectedAlert(null)} />
              </div>
              <div className="modal-body">
                <div className="text-muted mb-3">Active until {formatDateTime(selectedAlert.endsAt)}</div>
                <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{selectedAlert.content}</div>
                {selectedAlert.attachments.length > 0 && (
                  <div className="mt-4">
                    <strong>Attachments</strong>
                    <div className="list-group mt-2">
                      {selectedAlert.attachments.map((attachment, index) => (
                        <a className="list-group-item list-group-item-action" href={attachment.resolvedUrl} target="_blank" rel="noreferrer" key={`${attachment.url}-${index}`}>
                          <i className={`bi ${attachment.kind === "link" ? "bi-link-45deg" : "bi-paperclip"} me-2`} />
                          {attachment.originalName}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setSelectedAlert(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
